import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Calendar } from '@/components/ui/calendar';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { enGB, el } from 'date-fns/locale';

interface CalendarDatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDate: Date;
  onDateSelect: (date: Date | undefined) => void;
}

const CalendarDatePicker = ({
  open,
  onOpenChange,
  selectedDate,
  onDateSelect,
}: CalendarDatePickerProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const handleDateSelect = (date: Date | undefined) => {
    onDateSelect(date);
    if (date) {
      onOpenChange(false);
    }
  };

  const calendarContent = (
    <Calendar
      mode="single"
      locale={i18n.language === 'en' ? enGB : el}
      selected={selectedDate}
      defaultMonth={selectedDate}
      onSelect={handleDateSelect}
      initialFocus
      className="rounded-md border pointer-events-auto"
    />
  );

  if (isMobile || isTablet) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-full rounded-none p-0 flex flex-col">
          <SheetHeader className="p-4 border-b shrink-0">
            <SheetTitle className="text-center">{t('calendar.selectDate')}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 flex items-center justify-center p-6">
            <Calendar
              mode="single"
              locale={i18n.language === 'en' ? enGB : el}
              selected={selectedDate}
              defaultMonth={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
              className="rounded-md border pointer-events-auto scale-150 transform"
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-auto p-6">
        <DialogHeader>
          <DialogTitle>{t('calendar.selectDate')}</DialogTitle>
        </DialogHeader>
        {calendarContent}
      </DialogContent>
    </Dialog>
  );
};

export default CalendarDatePicker;
